import { useQuery } from "@tanstack/react-query";
import { callWithAuth, getStreakDays } from "@/api/Api";
import type { DayActivity } from "@/components/Streak/streakUtils";

/** YYYY-MM-DD in local time (matches streakUtils.localDateKey). */
function fmtDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * Days the user logged listening activity (iOS `useStreakDays` in
 * useHomeScreenData.ts → `weeklyListeningData`).
 *
 * Mirrors iOS: fetch `getStreakDays` over a window ending today and map each
 * `StatsData` row to `{ date, value: 1 }` for the home week strip. The strip
 * only needs the current calendar week, so we fetch a small window (the last 14
 * days) rather than iOS's 2-year backfill — iOS pulls the long range only to
 * populate its MMKV streak cache, which the web (react-query) doesn't keep.
 *
 * Returns `[]` until the user id is known, so the strip falls back to the
 * consecutive-streak derivation (identical for the common case).
 */
export function useStreakDays(userId: number | undefined): DayActivity[] {
  const today = new Date();
  const start = new Date(today);
  start.setDate(start.getDate() - 13); // 14-day window covers the visible week
  const startDate = fmtDate(start);
  const endDate = fmtDate(today);

  const query = useQuery({
    queryKey: ["streakDays", userId, startDate, endDate],
    queryFn: () => callWithAuth(getStreakDays, { startDate, endDate }),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // iOS parity (5 min)
  });

  // iOS maps each returned day to `{ date, value: 1 }` — a logged day counts as
  // activity regardless of the underlying value.
  return (query.data?.data ?? []).map((d) => ({ date: d.date, value: 1 }));
}
